"use client";

// ⚠️ One place that knows a request failed.
//
// `apiFetch` has always thrown a structured error — `${status}: ${body}` —
// so the diagnosis existed all along. It was thrown away 222 times by
// `.catch(() => setX(null))`, which turns every backend failure into an
// empty state. Paired with the server's silently swallowed exceptions, a
// fault was invisible on both sides of the wire at once, and the user saw a
// blank panel with no error anywhere to explain it.
//
// So this is deliberately NOT a new fetching library and NOT a rewrite of
// 443 call sites. It is a sink. `report()` records a failure; the banner
// renders whatever is in the sink; `apiFetch` reports automatically. A call
// site that still wants a null fallback can keep it — the difference is
// that the failure is now visible while the fallback renders.

export type ApiFailure = {
  key: string;          // the desk/module, so a reader knows WHAT is degraded
  message: string;      // what actually came back
  status: number | null;
  path: string;
  at: number;
};

type Listener = (failures: ApiFailure[]) => void;

const MAX = 8;                    // a wall of toasts is its own outage
const _failures = new Map<string, ApiFailure>();
const _listeners = new Set<Listener>();

function emit() {
  const list = [..._failures.values()].sort((a, b) => b.at - a.at);
  _listeners.forEach((l) => l(list));
}

/** Human-readable desk name from a path: /api/v5/funds/screener → "Funds". */
export function deskOf(path: string): string {
  const m = /\/api\/v[45]\/([^/?]+)/.exec(path);
  if (!m) return "Desk";
  return m[1].replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Strip the `${status}: ` prefix apiFetch adds, and any HTML error page. */
export function readableError(e: unknown): { status: number | null; message: string } {
  const raw = e instanceof Error ? e.message : String(e);
  const m = /^(\d{3}):\s*([\s\S]*)$/.exec(raw);
  const status = m ? Number(m[1]) : null;
  let body = (m ? m[2] : raw).trim();
  // a FastAPI error is {"detail": "..."}; anything else is shown as-is
  try {
    const j = JSON.parse(body) as { detail?: unknown };
    if (j && typeof j.detail === "string") body = j.detail;
  } catch {
    // not JSON — an HTML error page or a network message; keep the text but
    // do not paste a whole document into a toast
    if (body.startsWith("<")) body = "the server returned an error page";
  }
  if (body.length > 240) body = `${body.slice(0, 240)}…`;
  return { status, message: body || "no response from the server" };
}

export function report(path: string, e: unknown): ApiFailure {
  const { status, message } = readableError(e);
  const key = deskOf(path);
  const failure: ApiFailure = { key, message, status, path, at: Date.now() };
  // keyed by desk: ten failing rows on one desk is ONE outage, not ten
  _failures.set(key, failure);
  while (_failures.size > MAX) {
    const oldest = [..._failures.entries()].sort((a, b) => a[1].at - b[1].at)[0];
    if (!oldest) break;
    _failures.delete(oldest[0]);
  }
  emit();
  return failure;
}

export function clearFailure(key: string) {
  if (_failures.delete(key)) emit();
}

export function clearAll() {
  if (_failures.size) { _failures.clear(); emit(); }
}

/** A desk that succeeds clears its own banner — no stale "failed" after a retry. */
export function reportSuccess(path: string) {
  clearFailure(deskOf(path));
}

export function subscribe(l: Listener): () => void {
  _listeners.add(l);
  l([..._failures.values()].sort((a, b) => b.at - a.at));
  return () => { _listeners.delete(l); };
}

export function snapshot(): ApiFailure[] {
  return [..._failures.values()].sort((a, b) => b.at - a.at);
}
